import { z } from 'zod';

/**
 * Shape-only matcher for `YYYY-MM-DD`. Confirms the string is four digits,
 * a hyphen, two digits, a hyphen, two digits — nothing about whether the
 * month/day are in range. Use {@link isValidIsoDate} to also reject
 * calendar-invalid values like `2026-02-30`.
 */
export const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True iff `s` is a real calendar date in `YYYY-MM-DD` form.
 *
 * This exists because the regex alone is not enough and neither is
 * `Date.parse`. `Date.parse('2026-02-30')` does NOT fail — V8 rolls the
 * overflow forward to `2026-03-02` and returns a valid timestamp — so a
 * `.refine(s => !Number.isNaN(Date.parse(s)))` guard silently accepts
 * `2026-02-30`. Endpoints that only ever feed the value into Drizzle
 * comparisons get away with it (Postgres never sees the bad string), but
 * any query that casts `::date` in raw SQL throws `22008 date/time field
 * out of range` and surfaces as a 500 instead of a clean 400.
 *
 * We validate by reconstructing the date in UTC and checking each field
 * round-trips: if the input overflowed, the normalized Date reports a
 * different year/month/day and we reject.
 *
 * Accepted year range is 0100–9999. Years 0001–0099 are rejected as a
 * side effect of `Date.UTC`'s legacy two-digit-year handling (it maps
 * 0–99 to 1900–1999, so the round-trip never matches). That's fine for
 * our domain — every date here is 20xx — but don't reach for this on
 * pre-year-100 input without fixing the reconstruction first.
 */
export function isValidIsoDate(s: string): boolean {
  const m = ISO_DATE_REGEX.exec(s);
  if (!m) return false;
  const year = Number(s.slice(0, 4));
  const month = Number(s.slice(5, 7));
  const day = Number(s.slice(8, 10));
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const dt = new Date(Date.UTC(year, month - 1, day));
  return dt.getUTCFullYear() === year && dt.getUTCMonth() === month - 1 && dt.getUTCDate() === day;
}

/**
 * Zod schema for a strict `YYYY-MM-DD` calendar date string. Drop-in
 * replacement for the `z.string().regex(/^\d{4}-\d{2}-\d{2}$/)` blocks
 * scattered across the validators — this one also rejects calendar-invalid
 * dates (see {@link isValidIsoDate}).
 *
 *   const isoDate = isoDateString();
 *   z.object({ fromDate: isoDate, toDate: isoDate })
 *
 * Pass `message` to customize the user-facing error (e.g. `'Pick a date'`).
 */
export function isoDateString(message = 'must be a valid date in YYYY-MM-DD format') {
  return z.string().refine(isValidIsoDate, message);
}

// ---------------------------------------------------------------------------
// Week math
// ---------------------------------------------------------------------------
//
// These live here rather than in the web app's `timesheet-week.ts` because two
// unrelated features now depend on them agreeing exactly: the timesheet grid's
// week navigation, and the shared date-range widget's `current_week` preset.
// When they were a web-only module, `@clockwise/ui` could not import them and
// the alternative was a second, subtly different implementation — which is the
// exact drift that produced B_82 (a date-fns `startOfWeek` that disagreed with
// `mondayOf` on non-Monday "todays"). One copy, one behaviour.
//
// All three deliberately compute in UTC on the `YYYY-MM-DD` string form: the
// values are calendar dates, not instants, so a local-time round-trip would
// shift the answer for anyone west of UTC.

/** Add `delta` days to a YYYY-MM-DD date and return YYYY-MM-DD. */
export function addIsoDays(date: string, delta: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

/** Monday of the ISO week containing `date`. Returns YYYY-MM-DD.
 *  An unparseable input is echoed back unchanged (callers render it as-is). */
export function mondayOf(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  const day = d.getUTCDay(); // 0=Sun, 1=Mon, ... 6=Sat
  const offset = day === 0 ? -6 : 1 - day; // shift back to Monday
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}

/** Sunday at the end of the week starting on `weekStart`. */
export function sundayOf(weekStart: string): string {
  return addIsoDays(weekStart, 6);
}
